'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

import { useAuthModal } from './AuthModalProvider';
import { CUSTOM_CARDS, MODULES } from './modules.data';
import { SceneCanvas } from './SceneCanvas';

type StatPair = [string, string];

interface CheckText {
	name: string;
	desc: string;
	stats: StatPair[];
}

interface StageText {
	label: string;
	check: Record<string, CheckText>;
}

interface ModuleText {
	title: string;
	subtitle: string;
	vector: string;
	desc: string;
	stats: StatPair[];
	stage: Record<string, StageText>;
}

interface CustomText {
	label: string;
	tagline: string;
	badge: string;
	card: Record<string, { tag: string; title: string; desc: string }>;
}

function StatRows({ stats }: { stats: StatPair[] }) {
	return (
		<>
			{stats.map(([label, value]) => (
				<div
					key={label}
					className="border-border-subtle flex justify-between border-b pb-1"
				>
					<span className="label-mono">{label}</span>
					<span className="data-mono">{value}</span>
				</div>
			))}
		</>
	);
}

export function ModulesSection() {
	const t = useTranslations('Modules');
	const mt = t.raw('m') as Record<string, ModuleText>;
	const custom = t.raw('custom') as CustomText;
	const { open: openAuth } = useAuthModal();

	const [modIdx, setModIdx] = useState(0);
	const [stageIdx, setStageIdx] = useState(0);
	const [checkIdx, setCheckIdx] = useState(0);

	// Scroll affordance for the horizontal stepper: track whether more stages
	// are hidden off the left/right edges so we can fade + hint accordingly.
	const stepperRef = useRef<HTMLDivElement>(null);
	const [stepScroll, setStepScroll] = useState({ atStart: true, atEnd: false });
	useEffect(() => {
		const el = stepperRef.current;
		if (!el) return;
		// setState lives in scroll/resize callbacks (not the effect body), so it
		// runs after layout — no SSR drift and no set-state-in-effect.
		const update = () => {
			const { scrollLeft, scrollWidth, clientWidth } = el;
			const atStart = scrollLeft <= 1;
			const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
			setStepScroll((prev) =>
				prev.atStart === atStart && prev.atEnd === atEnd
					? prev
					: { atStart, atEnd },
			);
		};
		el.addEventListener('scroll', update, { passive: true });
		const ro = new ResizeObserver(update);
		ro.observe(el);
		if (el.firstElementChild) ro.observe(el.firstElementChild);
		return () => {
			el.removeEventListener('scroll', update);
			ro.disconnect();
		};
	}, []);

	// eslint-disable-next-line security/detect-object-injection -- modIdx is internal state bounded by MODULES selectors.
	const moduleDef = MODULES[modIdx];
	const moduleText = mt[moduleDef.id];
	// eslint-disable-next-line security/detect-object-injection -- stageIdx is internal state bounded by the selected module stages.
	const stageDef = moduleDef.stages[stageIdx];
	const stageText = moduleText.stage[stageDef.id];
	// eslint-disable-next-line security/detect-object-injection -- checkIdx is internal state bounded by the selected stage checks.
	const activeCheck = stageDef.checks[checkIdx];
	const activeCheckText = stageText.check[activeCheck.id];

	const selectModule = (i: number) => {
		setModIdx(i);
		setStageIdx(0);
		setCheckIdx(0);
	};
	const selectStage = (i: number) => {
		setStageIdx(i);
		setCheckIdx(0);
	};

	return (
		<section
			id="modules"
			className="relative scroll-mt-36 py-16 sm:scroll-mt-44 sm:py-24"
		>
			{/* Section header */}
			<div className="border-border mb-8 flex flex-col gap-2 border-b pb-4 sm:mb-12 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<div className="label-mono mb-2 sm:mb-3">{t('sectionCode')}</div>
					<h2 className="3xl:text-5xl text-2xl font-light tracking-tight sm:text-4xl">
						{t('heading')}
					</h2>
				</div>
				<div className="label-mono">{t('engineCode')}</div>
			</div>

			{/* Module selector */}
			<div className="mb-4 grid grid-cols-1 gap-3 sm:mb-6 sm:grid-cols-3 sm:gap-4">
				{MODULES.map((m, i) => {
					const active = i === modIdx;
					const text = mt[m.id];
					return (
						<button
							key={m.id}
							type="button"
							aria-pressed={active}
							onClick={() => selectModule(i)}
							className={`flex cursor-crosshair items-center gap-4 border p-4 text-left transition-colors ${
								active
									? 'border-fg bg-fg text-bg'
									: 'border-border hover:border-border-hover'
							}`}
						>
							<span className={`data-mono ${active ? 'text-bg/70' : ''}`}>
								{m.code}
							</span>
							<span className="flex min-w-0 flex-1 flex-col gap-1">
								<span className="text-base font-normal tracking-tight sm:text-lg">
									{text.title}
								</span>
								<span className={`label-mono ${active ? 'text-bg/72' : ''}`}>
									{text.subtitle}
								</span>
							</span>
							<span
								className={`label-mono self-center border px-2 py-1 whitespace-nowrap ${
									active ? 'border-bg/40 text-bg' : 'border-border'
								}`}
							>
								{m.tag}
							</span>
						</button>
					);
				})}
			</div>

			{/* Module body — two columns */}
			<div className="border-border bg-surface grid grid-cols-1 border lg:grid-cols-2">
				{/* LEFT: scene panel + module detail */}
				<div className="border-border flex flex-col border-b lg:border-r lg:border-b-0">
					<div className="border-border relative h-[260px] overflow-hidden border-b sm:h-[320px]">
						<SceneCanvas
							scene={stageDef.scene}
							className="absolute inset-0 h-full w-full"
						/>
						<div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-3">
							<div className="flex w-full items-start justify-between">
								<span className="label-mono bg-bg/90 text-fg px-1.5 py-0.5">
									{t('subjectWord')}: {moduleDef.code} / {stageDef.code}
								</span>
								<span className="label-mono bg-bg/90 text-fg px-1.5 py-0.5 text-base leading-none">
									+
								</span>
							</div>
							<div>
								<span className="data-mono bg-white px-1.5 py-0.5 text-black">
									{stageDef.en}
								</span>
							</div>
						</div>
					</div>

					<div className="flex flex-1 flex-col gap-4 p-4 sm:gap-6 sm:p-6">
						<div className="flex flex-col gap-2 sm:gap-3">
							<h3 className="3xl:text-3xl text-xl font-normal tracking-tight sm:text-2xl">
								{moduleText.title}
							</h3>
							<span className="label-mono">VECTOR: {moduleText.vector}</span>
							<p className="max-w-text text-body-sm text-accent mt-1 leading-[1.6]">
								{moduleText.desc}
							</p>
						</div>
						<div className="mt-auto flex flex-col gap-2 sm:gap-3">
							<StatRows stats={moduleText.stats} />
							<button
								type="button"
								onClick={() => openAuth('register')}
								className="border-border text-label tracking-label text-fg hover:border-fg focus-visible:ring-fg mt-2 flex min-h-11 w-full cursor-crosshair items-center justify-between border bg-transparent px-4 py-2.5 font-mono uppercase transition-colors focus-visible:ring-1 focus-visible:outline-none"
							>
								{t('cta')} <span>-&gt;</span>
							</button>
						</div>
					</div>
				</div>

				{/* RIGHT: sequence + stepper + stage + checks + detail */}
				<div className="flex flex-col gap-5 p-4 sm:gap-6 sm:p-6">
					<span className="label-mono">
						{`${t('sequenceHeading')} // ${moduleDef.code}`}
					</span>

					{/* Stepper — horizontally scrollable; edge fades + a pulsing
					    chevron hint that more stages are reachable by scrolling. */}
					<div className="relative -mx-1">
						<div
							ref={stepperRef}
							role="region"
							aria-label={t('sequenceHeading')}
							tabIndex={0}
							className="focus-visible:ring-fg overflow-x-auto overscroll-x-contain pb-1 focus-visible:ring-2 focus-visible:outline-none"
						>
							<div className="flex min-w-max items-start">
								{moduleDef.stages.map((s, i) => {
									const active = i === stageIdx;
									const last = i === moduleDef.stages.length - 1;
									return (
										<Fragment key={s.id}>
											<button
												type="button"
												aria-pressed={active}
												onClick={() => selectStage(i)}
												className="flex w-[88px] cursor-crosshair flex-col items-center gap-2 px-1 text-center"
											>
												<span
													className={`flex h-8 w-8 items-center justify-center border text-xs ${
														active
															? 'border-fg bg-fg text-bg'
															: 'border-border text-muted'
													}`}
													style={{ fontFamily: 'var(--font-mono)' }}
												>
													{s.code}
												</span>
												<span
													className={`text-body-sm leading-tight ${
														active ? 'text-fg' : 'text-accent'
													}`}
												>
													{moduleText.stage[s.id].label}
												</span>
												<span className="label-mono">{s.en}</span>
											</button>
											{!last && (
												<span
													aria-hidden="true"
													className="text-muted mt-3.5 flex items-center gap-1"
												>
													<span className="bg-border-hover h-px w-4" />
													<span className="text-[10px] leading-none">
														&rsaquo;
													</span>
												</span>
											)}
										</Fragment>
									);
								})}
							</div>
						</div>
						<div
							aria-hidden="true"
							className={`from-surface pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r to-transparent transition-opacity duration-200 ${stepScroll.atStart ? 'opacity-0' : 'opacity-100'}`}
						/>
						<div
							aria-hidden="true"
							className={`from-surface pointer-events-none absolute inset-y-0 right-0 flex w-14 items-center justify-end bg-gradient-to-l to-transparent pr-1 transition-opacity duration-200 ${stepScroll.atEnd ? 'opacity-0' : 'opacity-100'}`}
						>
							<span className="text-fg text-base leading-none motion-safe:animate-pulse">
								→
							</span>
						</div>
					</div>

					{/* Active stage label */}
					<div className="border-border flex items-center justify-between border-t pt-4">
						<span className="text-base font-normal tracking-tight">
							{t('stageWord')} {stageDef.code} — {stageText.label}
						</span>
						<span className="label-mono">
							{t('checksCount', { count: stageDef.checks.length })}
						</span>
					</div>

					{/* Check cards */}
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
						{stageDef.checks.map((check, i) => {
							const ct = stageText.check[check.id];
							const active = i === checkIdx;
							return (
								<button
									key={check.id}
									type="button"
									onClick={() => setCheckIdx(i)}
									aria-pressed={active}
									className={`flex cursor-crosshair flex-col gap-3 border p-4 text-left transition-colors ${
										active
											? 'border-fg'
											: 'border-border hover:border-border-hover'
									}`}
								>
									<div className="flex items-start justify-between gap-2">
										<span className="label-mono">{check.tag}</span>
										<span className="label-mono text-base leading-none">+</span>
									</div>
									<span className="text-body-sm font-normal tracking-tight">
										{ct.name}
									</span>
								</button>
							);
						})}
					</div>

					{/* Selected-check detail panel */}
					<div className="border-fg grid grid-cols-1 gap-6 border p-4 sm:grid-cols-2 sm:p-6">
						<div className="flex flex-col gap-2">
							<span className="label-mono">MODULE_DETAIL</span>
							<span className="label-mono">{activeCheck.tag}</span>
							<h4 className="text-lg font-normal tracking-tight sm:text-xl">
								{activeCheckText.name}
							</h4>
							<p className="text-body-sm text-accent mt-1 leading-[1.6]">
								{activeCheckText.desc}
							</p>
						</div>
						<div className="flex flex-col gap-2 sm:gap-3">
							<StatRows stats={activeCheckText.stats} />
							<button
								type="button"
								onClick={() => openAuth('register')}
								className="text-label tracking-label text-bg bg-fg focus-visible:ring-fg mt-auto flex min-h-11 w-full cursor-crosshair items-center justify-between px-4 py-2.5 font-mono uppercase transition-opacity hover:opacity-90 focus-visible:ring-1 focus-visible:outline-none"
							>
								{t('launchModule')} <span>-&gt;</span>
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Bottom — "Platform // Make it yours" customization cards (dark) */}
			<div className="mt-4 border border-white/10 bg-[#0a0a0b] p-4 text-white sm:mt-6 sm:p-6">
				<div className="mb-5 flex items-center justify-between gap-3 sm:mb-6">
					<span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] text-white/70 uppercase">
						<span
							className="h-1.5 w-1.5 rounded-full bg-white"
							aria-hidden="true"
						/>
						{`${custom.label} // ${custom.tagline}`}
					</span>
					<span className="font-mono text-[11px] tracking-[0.12em] text-white/70 uppercase">
						{custom.badge}
					</span>
				</div>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
					{CUSTOM_CARDS.map((card) => {
						const c = custom.card[card.id];
						return (
							<div
								key={card.id}
								className="flex flex-col gap-3 border border-white/20 p-4"
							>
								<div className="flex items-center justify-between gap-2">
									<span className="font-mono text-[13px] text-white/50">
										{card.num}
									</span>
									<span
										className={`font-mono text-[11px] tracking-[0.1em] uppercase ${
											card.highlight
												? 'font-medium text-white'
												: 'text-white/72'
										}`}
									>
										{c.tag}
									</span>
								</div>
								<h4 className="text-base font-normal tracking-tight text-white">
									{c.title}
								</h4>
								<p className="text-[13px] leading-[1.6] text-white/55">
									{c.desc}
								</p>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
